/**
 * Frontend Term Utilities for Multi-Country Support
 * Handles term display and formatting for Ghana (string) and Liberia (object) formats
 */

/**
 * Get term display name
 * @param {string|object} term - The term to display
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {string} - Display string
 */
export const getTermDisplay = (term, country = 'GH') => {
    if (!term) return 'No term';
    
    if (country === 'GH') {
        return typeof term === 'string' ? term : (term?.name || String(term));
    } else if (country === 'LR') {
        if (typeof term === 'object' && term.name) {
            return term.name;
        }
        return typeof term === 'string' ? term : 'Invalid term';
    }
    
    return String(term);
};

/**
 * Get term value for form submission or comparison
 * @param {string|object} term - The term
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {string|object} - Term value
 */
export const getTermValue = (term, country = 'GH') => {
    if (country === 'GH') {
        return typeof term === 'string' ? term : (term?.name || String(term));
    } else if (country === 'LR') {
        if (typeof term === 'object') {
            return term; // Return full object
        }
        // Fallback: create object from string (not recommended)
        return { 
            name: term, 
            startMonth: 1, 
            startYear: new Date().getFullYear(), 
            endMonth: 12, 
            endYear: new Date().getFullYear() 
        };
    }
    return term;
};

/**
 * Get term key for object property access (always returns string)
 * This is used for attendance storage where we need consistent string keys
 * @param {string|object} term - The term
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {string} - Term key as string
 */
export const getTermKey = (term, country = 'GH') => {
    if (country === 'GH') {
        return typeof term === 'string' ? term : (term?.name || String(term));
    } else if (country === 'LR') {
        // For Liberia, use the term name as the key for attendance storage
        if (typeof term === 'object' && term.name) {
            return term.name;
        }
        return typeof term === 'string' ? term : String(term);
    }
    return String(term);
};

/**
 * Format term with date range for Liberia
 * @param {object} term - Term object (Liberia format)
 * @returns {string} - Formatted string with dates
 */
export const formatTermWithDates = (term) => {
    if (typeof term !== 'object' || !term.name) {
        return getTermDisplay(term);
    }
    
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const startMonth = monthNames[term.startMonth - 1] || term.startMonth;
    const endMonth = monthNames[term.endMonth - 1] || term.endMonth;
    
    return `${term.name} (${startMonth} ${term.startYear} - ${endMonth} ${term.endYear})`;
};

/**
 * Check if term matches (for filtering/comparison)
 * @param {string|object} term1 - First term
 * @param {string|object} term2 - Second term
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {boolean} - True if terms match
 */
export const termsMatch = (term1, term2, country = 'GH') => {
    if (country === 'GH') {
        const val1 = typeof term1 === 'string' ? term1 : term1?.name;
        const val2 = typeof term2 === 'string' ? term2 : term2?.name;
        return val1 === val2;
    } else if (country === 'LR') {
        if (typeof term1 === 'object' && typeof term2 === 'object') {
            return term1.name === term2.name;
        }
        return false;
    }
    return false;
};

/**
 * Normalize terms array for display
 * @param {Array} terms - Array of terms
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {Array} - Array of display strings
 */
export const normalizeTermsForDisplay = (terms, country = 'GH') => {
    if (!Array.isArray(terms)) return [];
    
    return terms.map(term => getTermDisplay(term, country));
};

/**
 * Get term options for dropdown (MenuItem format)
 * @param {Array} terms - Array of terms
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {Array} - Array of { value, label } objects
 */
export const getTermOptions = (terms, country = 'GH') => {
    if (!Array.isArray(terms)) return [];
    
    if (country === 'GH') {
        return terms.map(term => {
            const value = typeof term === 'string' ? term : term?.name;
            return {
                value: value,
                label: value
            };
        });
    } else if (country === 'LR') {
        return terms.map(term => {
            if (typeof term === 'object' && term.name) {
                return {
                    value: term.name,
                    label: formatTermWithDates(term),
                    term: term // Include full object for reference
                };
            }
            return {
                value: term,
                label: term
            };
        });
    }
    
    return [];
};

/**
 * Find term by name or value
 * @param {Array} terms - Array of terms
 * @param {string|object} searchTerm - Term to find
 * @param {string} country - Country code ('GH' or 'LR')
 * @returns {string|object|null} - Found term or null
 */
export const findTerm = (terms, searchTerm, country = 'GH') => {
    if (!Array.isArray(terms)) return null;
    
    if (country === 'GH') {
        const searchValue = typeof searchTerm === 'string' ? searchTerm : searchTerm?.name;
        return terms.find(term => {
            const termValue = typeof term === 'string' ? term : term?.name;
            return termValue === searchValue;
        }) || null;
    } else if (country === 'LR') {
        const searchName = typeof searchTerm === 'object' ? searchTerm.name : searchTerm;
        return terms.find(term => {
            if (typeof term === 'object' && term.name) {
                return term.name === searchName;
            }
            return false;
        }) || null;
    }
    
    return null;
};

/**
 * Get terminology from school settings
 * @param {Array} schooldetails - School details from Redux
 * @returns {string} - Terminology (Term, Semester, Period, etc.)
 */
export const getTerminology = (schooldetails) => {
    if (!schooldetails || !Array.isArray(schooldetails) || schooldetails.length === 0) {
        return "Term"; // Default fallback
    }
    return schooldetails[0]?.termTerminology || "Term";
};

/**
 * Index of the school's active/current term. Falls back to last term when unset.
 * @param {object|null} school - Single school document
 * @returns {number} - Index or -1 if no terms
 */
export const getActiveTermIndex = (school) => {
    if (!school) return -1;
    const terms = Array.isArray(school.terms) ? school.terms : [];
    if (terms.length === 0) return -1;
    const n = parseInt(school.activeTermIndex, 10);
    if (Number.isFinite(n) && n >= 0 && n < terms.length) return n;
    return terms.length - 1;
};

/**
 * Active term entry (string or object).
 * @param {object|null} school
 * @returns {string|object|null}
 */
export const getActiveTerm = (school) => {
    const i = getActiveTermIndex(school);
    if (i < 0) return null;
    return school.terms[i];
};

/**
 * Display/name string for the active term (for selects and comparisons).
 * @param {object|null} school
 * @returns {string|null}
 */
export const getActiveTermName = (school) => {
    const term = getActiveTerm(school);
    if (term == null || term === "") return null;
    if (typeof term === "object" && term !== null) {
        return term.name != null ? String(term.name) : null;
    }
    return String(term);
};

/**
 * Resolve active term from Redux schooldetails_ array.
 * @param {Array} schooldetails_
 * @returns {string|object|null}
 */
export const getActiveTermFromSchoolDetails = (schooldetails_) => {
    if (!schooldetails_ || !Array.isArray(schooldetails_) || schooldetails_.length === 0) {
        return null;
    }
    return getActiveTerm(schooldetails_[0]);
};

/**
 * Active term name from Redux schooldetails_ (or "none" for legacy defaults).
 * @param {Array} schooldetails_
 * @param {string} [fallback="none"]
 */
export const getActiveTermNameFromSchoolDetails = (schooldetails_, fallback = "none") => {
    const name = schooldetails_ && schooldetails_[0] ? getActiveTermName(schooldetails_[0]) : null;
    return name != null && name !== "" ? name : fallback;
};

/**
 * Term entry after the active one (for preparing/sending next-term bills).
 * @param {object|null} school
 * @returns {string|object|null}
 */
export const getNextTerm = (school) => {
    if (!school) return null;
    const terms = Array.isArray(school.terms) ? school.terms : [];
    const i = getActiveTermIndex(school);
    if (i < 0 || i + 1 >= terms.length) return null;
    return terms[i + 1];
};

/**
 * Display name for the next term after active, or null if none recorded yet.
 */
export const getNextTermName = (school) => {
    const term = getNextTerm(school);
    if (term == null || term === "") return null;
    if (typeof term === "object" && term !== null) {
        return term.name != null ? String(term.name) : null;
    }
    return String(term);
};

/**
 * True when the active term looks like the last period of the year (e.g. "3rd Term"),
 * when next-term bills typically use the student's next class.
 */
export const isPromotionBillingTerm = (termName) => {
    const raw = String(termName || "").trim().toLowerCase();
    if (!raw) return false;
    if (raw.startsWith("3rd") || raw.startsWith("3rd ")) return true;
    if (raw.includes("3rd term") || raw.includes("third term")) return true;
    if (raw.startsWith("term 3") || raw === "t3") return true;
    return false;
};

/**
 * Options for prepare-bill / send-bill term pickers with Current / Next labels.
 * @param {object|null} school
 * @param {string} [terminology="Term"]
 * @returns {Array<{ value: string, label: string, role: 'current'|'next'|'other' }>}
 */
export const getBillTermOptions = (school, terminology = "Term") => {
    if (!school || !Array.isArray(school.terms)) return [];
    const activeIdx = getActiveTermIndex(school);
    return school.terms.map((term, index) => {
        const value =
            typeof term === "object" && term !== null && term.name != null
                ? String(term.name)
                : String(term);
        let role = "other";
        let suffix = "";
        if (index === activeIdx) {
            role = "current";
            suffix = ` (Current ${terminology})`;
        } else if (index === activeIdx + 1) {
            role = "next";
            suffix = ` (Next ${terminology})`;
        }
        return { value, label: `${value}${suffix}`, role, term };
    });
};

/**
 * Get term options for dropdown from database terms
 * @param {Array} schooldetails - School details from Redux
 * @returns {Array} - Array of { value, label } objects for MenuItem
 */
export const getTermOptionsFromDB = (schooldetails) => {
    if (!schooldetails || !Array.isArray(schooldetails) || schooldetails.length === 0) {
        return [];
    }
    
    const terms = schooldetails[0]?.terms || [];
    if (!Array.isArray(terms) || terms.length === 0) {
        return [];
    }
    
    return terms.map((term, index) => {
        // Handle both object (new) and string (old) formats
        if (typeof term === 'object' && term !== null && term.name) {
            return {
                value: term.name,
                label: term.name,
                term: term // Include full object for reference
            };
        }
        // String format (legacy)
        return {
            value: term,
            label: term
        };
    });
};

/**
 * Get term value for form submission
 * @param {string|object} term - Term from selection
 * @returns {string} - Term value to use in forms
 */
export const getTermValueForForm = (term) => {
    if (typeof term === 'object' && term !== null && term.name) {
        return term.name;
    }
    return term || "";
};




